import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { ArtifactBrowser } from '../components/artifacts/ArtifactBrowser';
import { ArtifactFilter } from '../components/artifacts/ArtifactFilter';
import { CreateArtifactModal } from '../components/artifacts/CreateArtifactModal';
import * as artifactService from '../services/artifactService';
import { ArtifactFilter as ArtifactFilterType } from '../types/artifact';
import { useTheme } from '../contexts/ThemeContext';
import { FiCode, FiBox, FiPlus } from 'react-icons/fi';
import { PersonaMarkdown } from '../components/personas/PersonaMarkdown';

export const Artifacts: React.FC = () => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<ArtifactFilterType>({});
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  
  const { data: artifacts, isLoading, error, refetch } = useQuery(
    ['artifacts', filter],
    () => artifactService.getAllArtifacts(filter)
  );
  
  return (
    <div>
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4 relative">
        {/* Add floating background effect */}
        <style jsx>{`
          .header-container {
            position: relative;
          }
          
          .header-container::before {
            content: '';
            position: absolute;
            top: -10px;
            left: -10px;
            right: -10px;
            bottom: -10px;
            background: ${darkMode ? 'rgba(15, 23, 42, 0.7)' : 'rgba(255, 255, 255, 0.7)'};
            border-radius: 12px;
            z-index: 0;
            box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1), 0 4px 8px rgba(59, 130, 246, 0.2);
            backdrop-filter: blur(8px);
            animation: pulse 4s infinite;
          }
          
          @keyframes pulse {
            0%, 100% {
              box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1), 0 4px 8px rgba(59, 130, 246, 0.2);
            }
            50% {
              box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15), 0 4px 12px rgba(59, 130, 246, 0.3);
            }
          }
        `}</style>
        <div className="header-container w-full flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="relative z-10">
            <h1 className="text-3xl font-bold flex items-center">
              <div className={`mr-3 p-2 rounded-lg ${
                darkMode 
                  ? 'bg-gradient-to-br from-blue-900 to-purple-900 shadow-inner shadow-blue-700/30' 
                  : 'bg-gradient-to-br from-blue-100 to-purple-100 shadow-inner shadow-blue-300/30'
              }`}>
                <FiBox className={`h-6 w-6 ${darkMode ? 'text-blue-300' : 'text-blue-700'}`} />
              </div>
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-500 to-purple-600 drop-shadow-sm">
                Artifacts
              </span>
            </h1>
            <p className={`mt-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'} max-w-xl`}>
              Browse and manage artifacts across all sessions
            </p>
          </div>
          
          <div className="flex space-x-2 relative z-10">
            {/* Artifactory button with tooltip */}
            <div className="group relative">
              <button 
                onClick={() => navigate('/artifactory')}
                className={`
                  ${darkMode 
                    ? 'bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700' 
                    : 'bg-gradient-to-r from-green-500 to-teal-500 hover:from-green-600 hover:to-teal-600'
                  } 
                  text-white px-5 py-2.5 rounded-md shadow-md hover:shadow-lg 
                  transition-all duration-200 flex items-center space-x-2
                  hover:shadow-green-500/50 dark:hover:shadow-green-800/30
                  font-bold whitespace-nowrap
                `}
              >
                <FiCode className="mr-2" />
                <span>Artifactory</span>
              </button>
              <div className={`absolute top-full right-0 mt-2 px-3 py-2 rounded shadow-lg z-10 
                text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none
                ${darkMode ? 'bg-gray-900 text-gray-200' : 'bg-white text-gray-800'} border ${darkMode ? 'border-gray-700' : 'border-gray-200'}
                whitespace-normal max-w-md w-80`}>
                <PersonaMarkdown content={`
## Artifactory

Create AI-powered code projects with:

- **Project Structure**: Generate complete, well-organized codebases
- **Intelligent Dependencies**: Files that understand their relationships
- **Hydration System**: Generate code with context from dependencies
- **Export Options**: Download your complete project as a ZIP file

Artifactory helps you rapidly prototype applications, libraries, or components with proper architecture and best practices.
                `} size="small" />
                <div className={`absolute bottom-full right-8 border-4 border-transparent 
                  ${darkMode ? 'border-b-gray-900' : 'border-b-white'}`}></div>
              </div>
            </div>
            
            {/* Create New Artifact button with tooltip */}
            <div className="group relative">
              <button
                onClick={() => setIsCreateModalOpen(true)}
                className={`
                  ${darkMode 
                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700' 
                    : 'bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600'
                  } 
                  text-white px-5 py-2.5 rounded-md shadow-md hover:shadow-lg 
                  transition-all duration-200 flex items-center space-x-2
                  hover:shadow-blue-500/50 dark:hover:shadow-blue-800/30
                  font-bold whitespace-nowrap
                `}
              >
                <FiPlus className="mr-2" />
                <span>Create New Artifact</span>
              </button>
              <div className={`absolute top-full right-0 mt-2 px-3 py-2 rounded shadow-lg z-10 
                text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none
                ${darkMode ? 'bg-gray-900 text-gray-200' : 'bg-white text-gray-800'} border ${darkMode ? 'border-gray-700' : 'border-gray-200'}
                whitespace-normal max-w-md w-80`}>
                <PersonaMarkdown content={`
## Create New Artifact

Create and manage various types of artifacts:

- **Documents**: Text, markdown, and rich content
- **Code**: Source code, scripts, and snippets
- **Data**: Structured data in various formats
- **Media**: Images, audio, and other media files

Artifacts can be shared across sessions and used as inputs for other processes.
                `} size="small" />
                <div className={`absolute bottom-full right-8 border-4 border-transparent 
                  ${darkMode ? 'border-b-gray-900' : 'border-b-white'}`}></div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-1">
          <ArtifactFilter
            filter={filter}
            onFilterChange={setFilter}
            resultCount={artifacts?.length}
          />
        </div>
        
        <div className="lg:col-span-3">
          {isLoading ? (
            <div className={`text-center py-10 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Loading artifacts...</div>
          ) : error ? (
            <div className={`text-center py-10 text-red-600 ${darkMode ? 'bg-red-900 bg-opacity-20' : ''}`}>Error loading artifacts</div>
          ) : (
            <ArtifactBrowser artifacts={artifacts || []} />
          )}
        </div>
      </div>
      
      <CreateArtifactModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={() => refetch()}
      />
    </div>
  );
};
